import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
/** Module `occupancy` (docs/modules/occupancy.md, spec 8.6.19, per site only in Phase 3). Owner: task S29. */
export const OccupancyLevel = z.enum(['none', 'green', 'amber', 'red']);
export const SiteOccupancy = z.object({
    siteId: Uuid,
    siteName: z.string(),
    count: z.number().int(),
    /** Of which sign-out-first members assumed in (estimated). */
    assumed: z.number().int(),
    capacity: z.number().int().nullable(),
    percent: z.number().nullable(),
    level: OccupancyLevel,
    byGroup: z.array(z.object({ groupId: Uuid, groupName: z.string(), count: z.number().int() })),
    estimated: z.boolean(),
    at: IsoDateTime,
});
export const OccupancyOverview = z.object({ sites: z.array(SiteOccupancy) });
/** Live channel `occupancy` (permission occupancy.view; kiosks and displays of the site). */
export const OccupancyLiveMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('occupancy.site'), site: SiteOccupancy }),
    z.object({ type: z.literal('refresh') }),
]);
export const OccupancyHistoryQuery = z.object({ siteId: Uuid, from: z.string(), to: z.string() });
export const OccupancyHistory = z.object({ siteId: Uuid, intervalMinutes: z.number().int(), samples: z.array(z.object({ at: IsoDateTime, count: z.number().int(), capacity: z.number().int().nullable() })) });
/** Kiosk config section `occupancy`. */
export const KioskOccupancySection = z.object({
    capacity: z.number().int().nullable(),
    amberPercent: z.number().int(),
    redPercent: z.number().int(),
    whenFull: z.enum(['off', 'warn', 'block_visitors']),
    count: z.number().int(),
});
export const OccupancySiteSettings = z.object({
    /** Safe maximum number of people on site; 0 = not set (no levels, no alerts). */
    capacity: z.number().int().min(0).default(0),
    amberPercent: z.number().int().min(1).max(200).default(80),
    redPercent: z.number().int().min(1).max(200).default(100),
    /** At the kiosk when the site is at red: show a warning and tell reception, or stop new visitor sign-ins. Members and evacuation are never blocked. */
    whenFull: z.enum(['off', 'warn', 'block_visitors']).default('off'),
    /** Count sign-out-first members assumed in. */
    countAssumed: z.boolean().default(true),
});
export const OccupancyTenantSettings = z.object({
    /** Minutes between history samples; 0 = no history. */
    historyIntervalMinutes: z.number().int().min(0).max(240).default(15),
    historyRetentionDays: z.number().int().min(0).default(730),
    /** Levels must fall this many percentage points below a level before it can fire again (one alert per crossing). */
    hysteresisPercent: z.number().int().min(0).max(50).default(5),
});
/** GET /occupancy/history/export (CSV by default). `from` and `to` are dates (YYYY-MM-DD, whole days in the site's zone) or ISO instants. */
export const OccupancyHistoryExportQuery = OccupancyHistoryQuery.extend({ format: z.enum(['csv', 'xlsx']).default('csv') });
//# sourceMappingURL=occupancy.js.map